import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export interface PuntoTemporal {
  dia: string;
  valores: number[];
}

interface Props {
  puntos: PuntoTemporal[];
  series: { nombre: string; color: string }[];
  /** Cómo se dice un valor: «12 consultas», «4 min». */
  formato?: (valor: number) => string;
  alto?: number;
}

const MARGEN = { arriba: 14, derecha: 14, abajo: 26, izquierda: 40 };

/**
 * Evolución en el tiempo.
 *
 * Una línea por serie sobre una rejilla discreta. El eje vertical **siempre
 * arranca en cero**: recortarlo exagera cualquier variación, y en una gráfica de
 * lecturas eso convierte una semana normal en una alarma.
 *
 * Trae capa de interacción —cruceta y globo— porque una gráfica en pantalla que
 * no responde al cursor obliga a estimar valores a ojo, y debajo va la misma
 * información en tabla para quien navega con lector de pantalla.
 */
export function GraficaTemporal({ puntos, series, formato, alto = 200 }: Props) {
  const { t, i18n } = useTranslation();
  const id = useId();
  const [activo, setActivo] = useState<number | null>(null);
  const [tablaVisible, setTablaVisible] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);
  const [ancho, setAncho] = useState(720);

  /**
   * El lienzo se dibuja al tamaño real del hueco, no a uno fijo estirado.
   *
   * Un `viewBox` fijo con `width: 100%` escala **todo** —incluidas las
   * tipografías y el grosor de las líneas—, así que la misma gráfica sale con
   * letras diminutas en una tarjeta angosta y enormes en una ancha. Medir el
   * contenedor cuesta unas líneas y hace que se vea igual en cualquier ancho.
   */
  useEffect(() => {
    const elemento = contenedor.current;
    if (!elemento) return;

    const observador = new ResizeObserver(([entrada]) => {
      const medido = entrada?.contentRect.width ?? 0;
      if (medido > 0) setAncho(Math.round(medido));
    });
    observador.observe(elemento);
    return () => observador.disconnect();
  }, []);
  const decir = formato ?? ((valor: number) => String(valor));
  const fecha = new Intl.DateTimeFormat(i18n.language, {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });

  const maximo = useMemo(() => {
    const tope = Math.max(1, ...puntos.flatMap((punto) => punto.valores));
    // Se redondea hacia arriba para que la línea superior de la rejilla sea un
    // número que alguien diría en voz alta, no 37.
    const magnitud = 10 ** Math.floor(Math.log10(tope));
    return Math.ceil(tope / magnitud) * magnitud;
  }, [puntos]);

  if (puntos.length === 0) return <p className="vacio">{t('charts.noData')}</p>;

  const x = (indice: number) =>
    MARGEN.izquierda +
    (indice * (ancho - MARGEN.izquierda - MARGEN.derecha)) / Math.max(1, puntos.length - 1);
  const y = (valor: number) =>
    MARGEN.arriba + (1 - valor / maximo) * (alto - MARGEN.arriba - MARGEN.abajo);

  const marcas = [0, maximo / 2, maximo];

  return (
    <div className="grafica" ref={contenedor}>
      <svg
        viewBox={`0 0 ${ancho} ${alto}`}
        width={ancho}
        height={alto}
        className="grafica__lienzo"
        role="img"
        aria-label={series.map((serie) => serie.nombre).join(', ')}
        onMouseLeave={() => setActivo(null)}
      >
        {marcas.map((marca) => (
          <g key={marca}>
            <line
              x1={MARGEN.izquierda}
              x2={ancho - MARGEN.derecha}
              y1={y(marca)}
              y2={y(marca)}
              className="grafica__rejilla"
            />
            <text x={MARGEN.izquierda - 8} y={y(marca) + 4} className="grafica__marca">
              {Math.round(marca)}
            </text>
          </g>
        ))}

        {series.map((serie, indiceSerie) => {
          const puntosLinea = puntos
            .map((punto, i) => `${x(i)},${y(punto.valores[indiceSerie] ?? 0)}`)
            .join(' ');
          return (
            <g key={serie.nombre}>
              <polyline points={puntosLinea} fill="none" stroke={serie.color} strokeWidth={2} />
              {/* El último punto lleva marca: es el dato que se busca primero. */}
              <circle
                cx={x(puntos.length - 1)}
                cy={y(puntos[puntos.length - 1]?.valores[indiceSerie] ?? 0)}
                r={4}
                fill={serie.color}
                stroke="var(--superficie)"
                strokeWidth={2}
              />
            </g>
          );
        })}

        {/* Franjas invisibles: el blanco de captura es mucho mayor que el punto,
            que es lo que hace usable el cursor sobre una serie densa. */}
        {puntos.map((punto, i) => (
          <rect
            key={punto.dia}
            x={x(i) - (ancho / puntos.length) / 2}
            y={0}
            width={ancho / puntos.length}
            height={alto}
            fill="transparent"
            onMouseEnter={() => setActivo(i)}
          />
        ))}

        {activo !== null && (
          <line
            x1={x(activo)}
            x2={x(activo)}
            y1={MARGEN.arriba}
            y2={alto - MARGEN.abajo}
            className="grafica__cruceta"
          />
        )}
      </svg>

      {activo !== null && puntos[activo] && (
        <div className="grafica__globo" role="status">
          <strong>{fecha.format(new Date(puntos[activo]!.dia))}</strong>
          {series.map((serie, indiceSerie) => (
            <span key={serie.nombre} className="grafica__globo-linea">
              <span className="grafica__punto" style={{ background: serie.color }} />
              {serie.nombre}: <strong>{decir(puntos[activo]!.valores[indiceSerie] ?? 0)}</strong>
            </span>
          ))}
        </div>
      )}

      {series.length > 1 && (
        <div className="grafica__leyenda">
          {series.map((serie) => (
            <span key={serie.nombre} className="grafica__leyenda-item">
              <span className="grafica__punto" style={{ background: serie.color }} />
              {serie.nombre}
            </span>
          ))}
        </div>
      )}

      <button
        className="boton boton--fantasma grafica__ver-tabla"
        type="button"
        aria-expanded={tablaVisible}
        aria-controls={id}
        onClick={() => setTablaVisible((v) => !v)}
      >
        {tablaVisible ? t('charts.hideTable') : t('charts.showTable')}
      </button>

      {tablaVisible && (
        <div className="tabla-envoltura" id={id}>
          <table>
            <thead>
              <tr>
                <th>{t('charts.day')}</th>
                {series.map((serie) => (
                  <th key={serie.nombre}>{serie.nombre}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {puntos.map((punto) => (
                <tr key={punto.dia}>
                  <td className="mono">{fecha.format(new Date(punto.dia))}</td>
                  {series.map((serie, indiceSerie) => (
                    <td key={serie.nombre} className="mono">
                      {decir(punto.valores[indiceSerie] ?? 0)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
